import {
  PANEL_ANGLES,
  PANEL_BACKDROPS,
  PANEL_BALLOONS,
  PANEL_BORDERS,
  PANEL_CAPTION_AT,
  PANEL_EFFECTS,
  PANEL_EFFECT_SIZES,
  PANEL_EXPRESSIONS,
  PANEL_FACINGS,
  PANEL_HOLDS,
  PANEL_LOOKS,
  PANEL_MARKS,
  PANEL_MARK_PLACES,
  PANEL_MASKS,
  PANEL_ON,
  PANEL_POSES,
  PANEL_POSE_HINTS,
  PANEL_PRESETS,
  PANEL_SETS,
  PANEL_SHOTS,
  PANEL_SIDES,
  PANEL_SLOTS,
  PANEL_SPEED_DIRS,
  PANEL_TEXT,
  POSE_HEADS,
  POSE_THUMBS,
} from '../../lib/story/panelIds';
import type { CondDef, PanelActorDef, PanelSpec } from '../../lib/story/types';
import type { Schemas } from '@argzombie/api-client';
import { issuesUnder, type FieldErrors } from '../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  SelectField,
  SelectInput,
  TextField,
  inputCls,
} from '../forms/fields';
import { isClipPose } from '../../lib/story/panelPoses';
import { ConditionPicker } from './ConditionPicker';
import { relErrors } from './relErrors';
import { TemplateInput } from './StoryBits';

const opts = (list: readonly string[]) => list.map((v) => ({ value: v, label: v || '(none)' }));

export type CellContext = {
  /** Roles a cell can use: the cast plus player and leader. */
  roles: string[];
  /** Hidden var and tell ids, for actor and mark `when`. */
  vars: string[];
  strings: Record<string, string>;
  setString: (key: string, value: string) => void;
};

/** Role choices for actors and speakers: the cast, member:N, plus anything already set. */
const roleOptions = (roles: string[], current?: string) => {
  const list = [...roles, 'member:2', 'member:3', 'member:4'];
  if (current && !list.includes(current)) list.push(current);
  return opts(list);
};

/** A stick-figure thumbnail per pose (a stand-in until the live preview): click to pick. */
export function PosePicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <div className="sm:col-span-2">
      <span className="block text-xs font-semibold uppercase tracking-wide">{label}</span>
      <div role="radiogroup" aria-label={label} className="mt-0.5 flex flex-wrap gap-1">
        {PANEL_POSES.map((p) => {
          const [cx, cy] = POSE_HEADS[p];
          return (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={value === p}
              aria-label={p}
              title={`${p}: ${PANEL_POSE_HINTS[p]}`}
              onClick={() => onChange(p)}
              className={`flex flex-col items-center rounded border px-0.5 ${value === p ? 'border-ink bg-benday/30' : 'border-newsprint bg-white hover:bg-caption/30'}`}
            >
              <svg width={24} height={32} viewBox="0 0 24 32" aria-hidden>
                <circle cx={cx} cy={cy} r={3} fill="none" stroke="#15121a" strokeWidth={1.4} />
                <path
                  d={POSE_THUMBS[p]}
                  fill="none"
                  stroke="#15121a"
                  strokeWidth={1.4}
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-[9px] leading-none">{p}</span>
            </button>
          );
        })}
      </div>
      {value && !(PANEL_POSES as readonly string[]).includes(value) && (
        <p className="text-xs text-blam">Unknown pose '{value}'.</p>
      )}
    </div>
  );
}

/**
 * The cell editor (panels.html §4 "Admin"): forms for layout, shot, cast (pose thumbnails, expression, marks behind a tell), set,
 * lighting, effects, caption and dialogue. Caption and balloon text are string keys into the chain's `strings`; the editor edits the
 * English source in place with the template tokens highlighted. `errors` are keyed under `base` (`beats.b1.cells.0.panel`).
 */
type ShotAdjust = Schemas['PanelShotAdjustDef'];
type ActorNudge = Schemas['PanelActorNudgeDef'];
/** ADR 0059 phase 2: `shot.adjust` members, labels, steps and defaults (shared-cs `PanelShotAdjustDef`). */
const ADJUST_FIELDS: [keyof ShotAdjust, string, number, number][] = [
  ['yaw', 'Adjust yaw (orbit °)', 1, 0],
  ['pitch', 'Adjust pitch (orbit °)', 1, 0],
  ['dolly', 'Adjust dolly (× distance)', 0.05, 1],
  ['fov', 'Adjust FOV (± °)', 1, 0],
  ['panX', 'Adjust pan X (share of frame)', 0.01, 0],
  ['panY', 'Adjust pan Y (share of frame)', 0.01, 0],
];
const NUDGE_FIELDS: [keyof ActorNudge, string, number][] = [
  ['dx', 'Nudge right (m)', 0.05],
  ['dz', 'Nudge forward (m)', 0.05],
  ['turn', 'Turn (°)', 5],
];
/** Set one member (undefined removes it); an object left with no members becomes null (the field left out). */
function patchOrNull<T extends object>(
  cur: T | null | undefined,
  k: keyof T,
  v: number | null | undefined,
): T | null {
  const next: Record<string, unknown> = { ...(cur ?? {}), [k]: v ?? undefined };
  Object.keys(next).forEach((key) => {
    if (next[key] === undefined) delete next[key];
  });
  return Object.keys(next).length ? (next as T) : null;
}

export function CellEditor({
  value,
  onChange,
  ctx,
  errors,
  base,
}: {
  value: PanelSpec;
  onChange: (next: PanelSpec) => void;
  ctx: CellContext;
  errors: FieldErrors;
  base: string;
}) {
  const p = value;
  const e = (path: string) => errors[`${base}.${path}`];
  const set = <K extends keyof PanelSpec>(k: K, v: PanelSpec[K]) => onChange({ ...p, [k]: v });
  const layout = p.layout ?? {};
  const shot = p.shot ?? {};
  const setting = p.set ?? {};
  const light = p.lighting ?? {};
  const roleSet = new Set(ctx.roles);
  const keyFor = (suffix: string) => `${p.id || 'cell'}.${suffix}`;

  /** A text bound to a string key: edit the key (mono) and its English source (highlighted). */
  const stringText = (
    label: string,
    key: string | undefined,
    setKey: (k: string) => void,
    max: number,
    fallbackKey: string,
  ) => {
    const k = key ?? '';
    return (
      <div className="grid gap-1 sm:col-span-2 sm:grid-cols-[10rem_minmax(0,1fr)]">
        <Field label={`${label} key`}>
          {(id) => (
            <input
              id={id}
              className={`${inputCls} font-mono`}
              value={k}
              placeholder={fallbackKey}
              onChange={(ev) => setKey(ev.target.value)}
            />
          )}
        </Field>
        <TemplateInput
          label={`${label} text (English source)`}
          value={k ? (ctx.strings[k] ?? '') : ''}
          onChange={(v) => {
            const useKey = k || fallbackKey;
            if (!k) setKey(useKey);
            ctx.setString(useKey, v);
          }}
          roles={roleSet}
          max={max}
        />
      </div>
    );
  };

  return (
    <div data-testid="cell-editor">
      <Fieldset legend="Layout">
        <TextField
          label="Cell id"
          mono
          value={p.id}
          issues={e('id')}
          onChange={(v) => set('id', v)}
        />
        <SelectField
          label="Slot"
          value={layout.slot ?? 'auto'}
          options={opts(PANEL_SLOTS)}
          issues={e('layout.slot')}
          onChange={(v) => set('layout', { ...layout, slot: v ?? 'auto' })}
        />
        {layout.slot === 'inset' && (
          <TextField
            label="Inset of (cell id)"
            mono
            value={layout.insetOf}
            issues={e('layout.insetOf')}
            onChange={(v) => set('layout', { ...layout, insetOf: v })}
          />
        )}
        <SelectField
          label="Border"
          value={layout.border ?? 'ink'}
          options={opts(PANEL_BORDERS)}
          onChange={(v) => set('layout', { ...layout, border: v ?? 'ink' })}
        />
        <SelectField
          label="Mask"
          value={layout.mask ?? ''}
          options={opts(PANEL_MASKS)}
          onChange={(v) => set('layout', { ...layout, mask: v ?? '' })}
        />
        <CheckboxField
          label="Page break before"
          checked={!!layout.pageBreak}
          onChange={(v) => set('layout', { ...layout, pageBreak: v })}
        />
        <CheckboxField
          label="Decision cell (the choice is drawn on it)"
          checked={!!p.decision}
          onChange={(v) => set('decision', v)}
        />
      </Fieldset>

      <Fieldset legend="Shot">
        <SelectField
          label="Type"
          value={shot.type ?? 'medium'}
          options={opts(PANEL_SHOTS)}
          issues={e('shot.type')}
          onChange={(v) => set('shot', { ...shot, type: v ?? 'medium' })}
        />
        <SelectField
          label="Subject"
          value={shot.subject ?? ''}
          none="(group)"
          options={roleOptions(ctx.roles, shot.subject)}
          issues={e('shot.subject')}
          onChange={(v) => set('shot', { ...shot, subject: v ?? '' })}
        />
        {shot.type === 'ots' && (
          <SelectField
            label="From (near role)"
            value={shot.from ?? ''}
            options={roleOptions(ctx.roles, shot.from)}
            issues={e('shot.from')}
            onChange={(v) => set('shot', { ...shot, from: v ?? '' })}
          />
        )}
        {shot.type === 'extreme_close' && (
          <SelectField
            label="On"
            value={shot.on ?? ''}
            none="face"
            options={opts(PANEL_ON)}
            onChange={(v) => set('shot', { ...shot, on: v ?? '' })}
          />
        )}
        <SelectField
          label="Angle"
          value={shot.angle ?? 'eye'}
          options={opts(PANEL_ANGLES)}
          onChange={(v) => set('shot', { ...shot, angle: v ?? 'eye' })}
        />
        <NumberField
          label="Dutch (-15..15°)"
          integer
          min={-15}
          max={15}
          value={shot.dutch}
          issues={e('shot.dutch')}
          onChange={(v) => set('shot', { ...shot, dutch: v ?? 0 })}
        />
        <SelectField
          label="Side (180° rule)"
          value={shot.side ?? 'auto'}
          options={opts(PANEL_SIDES)}
          onChange={(v) => set('shot', { ...shot, side: v ?? 'auto' })}
        />
        {/* ADR 0059 phase 2 (card #302): an adjust after the solver, relative to its camera; left out = the solved shot. */}
        <div className="sm:col-span-3 grid gap-2 sm:grid-cols-3">
          {ADJUST_FIELDS.map(([k, label, step, def]) => (
            <NumberField
              key={k}
              label={label}
              nullable
              step={step}
              placeholder={String(def)}
              value={shot.adjust?.[k]}
              issues={e(`shot.adjust.${k}`)}
              onChange={(v) => set('shot', { ...shot, adjust: patchOrNull(shot.adjust, k, v) })}
            />
          ))}
          {shot.adjust ? (
            <button
              type="button"
              className="text-xs underline"
              onClick={() => set('shot', { ...shot, adjust: null })}
            >
              Reset adjust to the solved shot
            </button>
          ) : null}
        </div>
      </Fieldset>

      <Fieldset legend="Cast">
        <ListEditor
          label="Actors (≤ 6, zombies included)"
          items={p.actors ?? []}
          onChange={(v) => set('actors', v)}
          what="actor"
          issues={e('actors')}
          make={(): PanelActorDef => ({
            role: ctx.roles[0] ?? 'player',
            mark: 'center',
            facing: 'camera',
            pose: 'stand',
            pairWith: '',
            expression: 'neutral',
            lookAt: 'camera',
            hold: 'weapon',
            marks: [],
            when: null,
          })}
          row={(a, i, put) => (
            <ActorEditor
              value={a}
              onChange={put}
              ctx={ctx}
              errors={errors}
              base={`${base}.actors.${i}`}
              others={(p.actors ?? []).filter((_, j) => j !== i).map((x) => x.role ?? '')}
            />
          )}
        />
      </Fieldset>

      <Fieldset legend="Set and lighting">
        <SelectField
          label="Set"
          value={setting.kind ?? 'room'}
          options={opts(PANEL_SETS)}
          issues={e('set.kind')}
          onChange={(v) => set('set', { ...setting, kind: v ?? 'room' })}
        />
        {setting.kind === 'kit_room' && (
          <TextField
            label="Room template"
            mono
            value={setting.template}
            issues={e('set.template')}
            onChange={(v) => set('set', { ...setting, template: v })}
          />
        )}
        <CheckboxField
          label="Dressing"
          checked={setting.dressing ?? true}
          onChange={(v) => set('set', { ...setting, dressing: v })}
        />
        <SelectField
          label="Lighting"
          value={light.preset ?? 'day'}
          options={opts(PANEL_PRESETS)}
          issues={e('lighting.preset')}
          onChange={(v) => set('lighting', { ...light, preset: v ?? 'day' })}
        />
        <TextField
          label="Light from"
          mono
          value={light.from}
          issues={e('lighting.from')}
          onChange={(v) => set('lighting', { ...light, from: v })}
          hint="role:player, or a mark."
        />
        {light.preset === 'silhouette' && (
          <SelectField
            label="Backdrop"
            value={light.backdrop ?? ''}
            options={opts(PANEL_BACKDROPS)}
            issues={e('lighting.backdrop')}
            onChange={(v) => set('lighting', { ...light, backdrop: v ?? '' })}
          />
        )}
      </Fieldset>

      <Fieldset legend="Effects">
        <ListEditor
          label="Effects"
          items={p.effects ?? []}
          onChange={(v) => set('effects', v)}
          what="effect"
          issues={e('effects')}
          make={() => ({
            type: 'sfx',
            word: keyFor('sfx'),
            at: '',
            size: 'm',
            style: '',
            dir: '',
            around: '',
            amount: 0,
          })}
          row={(fx, i, put) => (
            <div className="grid gap-1 sm:grid-cols-3">
              <SelectInput
                label="Effect type"
                value={fx.type ?? ''}
                options={opts(PANEL_EFFECTS)}
                onChange={(v) => put({ ...fx, type: v ?? '' })}
              />
              {fx.type === 'sfx' && (
                <>
                  <input
                    aria-label="Sound word key"
                    className={`${inputCls} font-mono`}
                    value={fx.word ?? ''}
                    onChange={(ev) => put({ ...fx, word: ev.target.value })}
                  />
                  <input
                    aria-label="Sound word text"
                    className={inputCls}
                    placeholder="THUD"
                    value={ctx.strings[fx.word ?? ''] ?? ''}
                    onChange={(ev) => ctx.setString(fx.word || keyFor('sfx'), ev.target.value)}
                  />
                </>
              )}
              {fx.type === 'speed_lines' && (
                <>
                  <SelectInput
                    label="Direction"
                    value={fx.dir ?? ''}
                    options={opts(PANEL_SPEED_DIRS)}
                    onChange={(v) => put({ ...fx, dir: v ?? '' })}
                  />
                  <input
                    aria-label="Around role"
                    className={`${inputCls} font-mono`}
                    placeholder="around role"
                    value={fx.around ?? ''}
                    onChange={(ev) => put({ ...fx, around: ev.target.value })}
                  />
                </>
              )}
              {fx.type === 'vignette' ? (
                <input
                  aria-label="Vignette amount"
                  type="number"
                  step={0.05}
                  min={0}
                  max={1}
                  className={inputCls}
                  value={fx.amount ?? 0}
                  onChange={(ev) => put({ ...fx, amount: Number(ev.target.value) })}
                />
              ) : (
                <>
                  <input
                    aria-label="Effect at"
                    className={`${inputCls} font-mono`}
                    placeholder="role:companion"
                    value={fx.at ?? ''}
                    onChange={(ev) => put({ ...fx, at: ev.target.value })}
                  />
                  <SelectInput
                    label="Size"
                    value={fx.size ?? ''}
                    none="(default)"
                    options={opts(PANEL_EFFECT_SIZES)}
                    onChange={(v) => put({ ...fx, size: v ?? '' })}
                  />
                </>
              )}
              <div className="sm:col-span-3">
                <IssueList issues={issuesUnder(errors, `${base}.effects.${i}`)} />
              </div>
            </div>
          )}
        />
      </Fieldset>

      <Fieldset legend="Caption and dialogue">
        {p.caption ? (
          <>
            {stringText(
              'Caption',
              p.caption.text,
              (k) => set('caption', { ...p.caption, text: k }),
              PANEL_TEXT.captionChars,
              keyFor('cap'),
            )}
            <SelectField
              label="Caption at"
              value={p.caption.at ?? 'top'}
              options={opts(PANEL_CAPTION_AT)}
              onChange={(v) => set('caption', { ...p.caption, at: v ?? 'top' })}
            />
            <button
              type="button"
              className="text-xs text-blam underline"
              onClick={() => set('caption', null)}
            >
              Remove caption
            </button>
            <IssueList issues={issuesUnder(errors, `${base}.caption`)} />
          </>
        ) : (
          <button
            type="button"
            className="text-xs underline"
            onClick={() => set('caption', { text: keyFor('cap'), at: 'top' })}
          >
            Add a caption
          </button>
        )}
        <ListEditor
          label="Balloons (≤ 3, ≤ 90 characters)"
          items={p.dialogue ?? []}
          onChange={(v) => set('dialogue', v)}
          what="balloon"
          issues={e('dialogue')}
          make={() => ({
            speaker: ctx.roles[0] ?? 'player',
            text: keyFor(`d${(p.dialogue?.length ?? 0) + 1}`),
            kind: 'speech',
            order: (p.dialogue?.length ?? 0) + 1,
          })}
          row={(l, i, put) => (
            <div className="grid gap-1 sm:grid-cols-2">
              <SelectInput
                label="Speaker"
                value={l.speaker ?? ''}
                options={roleOptions(ctx.roles, l.speaker)}
                onChange={(v) => put({ ...l, speaker: v ?? '' })}
              />
              <SelectInput
                label="Balloon kind"
                value={l.kind ?? 'speech'}
                options={opts(PANEL_BALLOONS)}
                onChange={(v) => put({ ...l, kind: v ?? 'speech' })}
              />
              {stringText(
                `Balloon ${i + 1}`,
                l.text,
                (k) => put({ ...l, text: k }),
                PANEL_TEXT.balloonChars,
                keyFor(`d${i + 1}`),
              )}
              <div className="sm:col-span-2">
                <IssueList issues={issuesUnder(errors, `${base}.dialogue.${i}`)} />
              </div>
            </div>
          )}
        />
      </Fieldset>
    </div>
  );
}

function ActorEditor({
  value: a,
  onChange,
  ctx,
  errors,
  base,
  others,
}: {
  value: PanelActorDef;
  onChange: (next: PanelActorDef) => void;
  ctx: CellContext;
  errors: FieldErrors;
  base: string;
  others: string[];
}) {
  const put = (patch: Partial<PanelActorDef>) => onChange({ ...a, ...patch });
  const e = (f: string) => errors[`${base}.${f}`];
  const sub = (f: string) => relErrors(errors, `${base}.${f}`);
  const places = [...PANEL_MARK_PLACES, ...others.flatMap((r) => [`near:${r}`, `behind:${r}`])];
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <SelectField
        label="Role"
        value={a.role ?? ''}
        options={roleOptions(ctx.roles, a.role)}
        issues={e('role')}
        onChange={(v) => put({ role: v ?? '' })}
      />
      <SelectField
        label="Mark"
        value={a.mark ?? 'center'}
        options={opts(a.mark && !places.includes(a.mark) ? [...places, a.mark] : places)}
        onChange={(v) => put({ mark: v ?? 'center' })}
      />
      <SelectField
        label="Facing"
        value={a.facing ?? 'camera'}
        options={opts([...PANEL_FACINGS, ...others.map((r) => `role:${r}`)])}
        onChange={(v) => put({ facing: v ?? 'camera' })}
      />
      <PosePicker label="Pose" value={a.pose ?? ''} onChange={(v) => put({ pose: v })} />
      {/* Card #303: any clip frame (clip:<clipId>@<0..1>[:mirror]); Story Lab has the picker and scrub bar. */}
      <TextField
        label="Pose or clip frame"
        hint="A pose above, or clip:<clipId>@<0..1> with an optional :mirror (e.g. clip:Talk_Gesture@0.35)"
        mono
        value={a.pose}
        onChange={(v) => put(isClipPose(v) ? { pose: v, poseVariant: undefined } : { pose: v })}
      />
      <IssueList issues={e('pose')} />
      {!isClipPose(a.pose) && (
        <NumberField
          label="Pose variant"
          nullable
          min={0}
          step={1}
          placeholder="0"
          value={a.poseVariant}
          issues={e('poseVariant')}
          onChange={(v) => put({ poseVariant: v ? Math.round(v) : undefined })}
        />
      )}
      {(a.pose === 'grab' || a.pose === 'dragged' || a.pairWith) && (
        <SelectField
          label="Pair with"
          value={a.pairWith ?? ''}
          none="(none)"
          options={opts(others)}
          issues={e('pairWith')}
          onChange={(v) => put({ pairWith: v ?? '' })}
        />
      )}
      <SelectField
        label="Expression"
        value={a.expression ?? ''}
        none="(neutral)"
        options={opts(PANEL_EXPRESSIONS)}
        issues={e('expression')}
        onChange={(v) => put({ expression: v ?? '' })}
      />
      {/* Card #304: how strongly the face shows (left out = 1, today's full face) and an optional second shape blended over it. */}
      <NumberField
        label="Expression amount (0..1)"
        nullable
        min={0}
        max={1}
        step={0.05}
        placeholder="1"
        value={a.expressionAmount}
        issues={e('expressionAmount')}
        onChange={(v) => put({ expressionAmount: v ?? undefined })}
      />
      <SelectField
        label="Second expression"
        value={a.expression2 ?? ''}
        none="(none)"
        options={opts(PANEL_EXPRESSIONS.filter((x) => x !== 'turned'))}
        issues={e('expression2')}
        onChange={(v) =>
          put(v ? { expression2: v } : { expression2: undefined, expression2Amount: undefined })
        }
      />
      {a.expression2 ? (
        <NumberField
          label="Second amount (0..1)"
          nullable
          min={0}
          max={1}
          step={0.05}
          placeholder="1"
          value={a.expression2Amount}
          issues={e('expression2Amount')}
          onChange={(v) => put({ expression2Amount: v ?? undefined })}
        />
      ) : null}
      <SelectField
        label="Look at"
        value={a.lookAt ?? 'camera'}
        options={opts([...PANEL_LOOKS, ...others.map((r) => `role:${r}`)])}
        onChange={(v) => put({ lookAt: v ?? 'camera' })}
      />
      <SelectField
        label="Hold"
        value={a.hold ?? 'weapon'}
        options={opts(PANEL_HOLDS)}
        issues={e('hold')}
        onChange={(v) => put({ hold: v ?? 'weapon' })}
      />
      {/* ADR 0059 phase 2 (card #302): a nudge from the mark (metres in its frame) and a turn; left out = today's spot. */}
      {NUDGE_FIELDS.map(([k, label, step]) => (
        <NumberField
          key={k}
          label={label}
          nullable
          step={step}
          placeholder="0"
          value={a.nudge?.[k]}
          issues={e(`nudge.${k}`)}
          onChange={(v) => put({ nudge: patchOrNull(a.nudge, k, v) })}
        />
      ))}
      {a.nudge ? (
        <button type="button" className="text-xs underline" onClick={() => put({ nudge: null })}>
          Reset nudge
        </button>
      ) : null}
      <div className="sm:col-span-3">
        <ListEditor
          label="Marks (optionally behind a tell)"
          items={a.marks ?? []}
          onChange={(v) => put({ marks: v })}
          what="mark"
          make={() => ({ id: 'blood_arm', when: null })}
          row={(m, j, putMark) => (
            <div>
              <SelectInput
                label="Mark"
                value={m.id ?? ''}
                options={opts(PANEL_MARKS)}
                onChange={(v) => putMark({ ...m, id: v ?? '' })}
              />
              <ConditionPicker
                label={`Mark ${j + 1} when`}
                value={m.when}
                onChange={(c: CondDef | null) => putMark({ ...m, when: c })}
                hints={{ vars: ctx.vars, allowRaid: false }}
                issues={sub(`marks.${j}.when`)}
              />
              <IssueList issues={e(`marks.${j}.id`)} />
            </div>
          )}
        />
      </div>
      <div className="sm:col-span-3">
        <ConditionPicker
          label="Actor shown when"
          value={a.when}
          onChange={(c) => put({ when: c })}
          hints={{ vars: ctx.vars, allowRaid: false }}
          issues={sub('when')}
        />
      </div>
    </div>
  );
}
